import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { Card } from 'react-bootstrap';
import Loader from '../Loader';
import * as MinistryReportApi from '../../api/MinistryReportApi';
import { toast } from 'react-toastify';
import IncidentListingModal from './IncidentListingModal';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

const categoryColors = {
  drivedownload: 'rgb(254, 195, 190)',
  contentdelivery: 'rgb(224,224,224)',
  contentprocess: 'rgb(255,224,143)',
  entryofcandidates: 'rgb(203,235,174)',
  registration: 'rgb(179,218,244)',
  aadharauthentication: 'rgb(187,211,218)',
  examcommencement: 'rgb(223,194,238)',
  allowedcandidateslogin: 'rgb(121,159,229)',
  examcompletion: 'rgb(144,132,219)',
  lockunlockincidents: 'rgb(122,234,203)',
};

export const MinsitryScatterChart = ({
  data,
  height = 380,
  width = 1440,
  isLoading,
}) => {
  const chartRef = useRef(null);
  const [modalData, setModalData] = useState([]);
  const DEFAULT_INCIDENT_DETAILS_MODAL = {
    center_id: null,
    show: false,
    title: 'Incident Details',
  };
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] = useState(
    DEFAULT_INCIDENT_DETAILS_MODAL
  );

  const [filter, setFilter] = useState({
    id: null,
  });

  const fetchModalData = async () => {
    const response = await MinistryReportApi.getIncidentById(filter);

    if (response?.success) {
      setModalData([response?.data]);
    } else {
      toast.error(`Error fetching stage incidents:  ${response?.message}`);
    }
  };

  useEffect(() => {
    if (filter.id) {
      fetchModalData();
    }
  }, [filter.id]);

  // const chartData = useMemo(() => {
  //   if (!data || !Array.isArray(data)) {
  //     console.error('Invalid or missing data:', data);
  //     return { datasets: [] };
  //   }

  //   const categories = [...new Set(data.map((item) => item.category))];
  //   const categoryMap = categories.reduce((acc, category, index) => {
  //     acc[category] = index + 1;
  //     return acc;
  //   }, {});

  //   const parseTime = (timeStr) => {
  //     const [hours, minutes] = timeStr.match(/\d+/g).map(Number);
  //     const isPM = timeStr.includes('PM');
  //     return (
  //       (isPM && hours !== 12
  //         ? hours + 12
  //         : hours === 12 && !isPM
  //         ? 0
  //         : hours) +
  //       minutes / 60
  //     );
  //   };

  //   const majorDataset = [];
  //   const minorDataset = [];

  //   data.forEach((categoryObj) => {
  //     const normalizedCategory = categoryObj.category
  //       .toLowerCase()
  //       .replace(/\s+/g, ''); // Normalize category
  //     categoryObj.incidents.forEach((incident) => {
  //       const color =
  //         categoryColors[
  //           normalizedCategory.toLowerCase().replace(/[^a-z0-9]/g, '')
  //         ] || 'gray'; // Ensure fallback is a visible color

  //       const dataPoint = {
  //         x: parseTime(incident.timestamp),
  //         y: categoryMap[categoryObj.category],
  //         label: `${categoryObj.category} - ${incident.timestamp}`,
  //         rawData: incident,
  //         categoryColor: color, // Store the category color
  //       };

  //       if (incident.type_of_issue === 'Major') {
  //         majorDataset.push(dataPoint);
  //       } else if (incident.type_of_issue === 'Minor') {
  //         minorDataset.push(dataPoint);
  //       }
  //     });
  //   });

  //   return {
  //     datasets: [
  //       {
  //         label: 'Major',
  //         data: majorDataset,
  //         pointRadius: 6,
  //         pointBackgroundColor: (ctx) => ctx.raw?.categoryColor || 'red', // ✅ Apply colors
  //       },
  //       {
  //         label: 'Minor',
  //         data: minorDataset,
  //         pointRadius: 6,
  //         pointBackgroundColor: (ctx) => ctx.raw?.categoryColor || 'blue', // ✅ Apply colors
  //       },
  //     ],
  //     categoryMap,
  //   };
  // }, [data]);
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      console.error('Invalid or missing data:', data);
      return { datasets: [] };
    }

    const categories = [...new Set(data.map((item) => item.category))];
    const categoryMap = categories.reduce((acc, category, index) => {
      acc[category] = index + 1;
      return acc;
    }, {});

    const parseTime = (timeStr) => {
      const [hours, minutes] = timeStr.match(/\d+/g).map(Number);
      const isPM = timeStr.includes('PM');
      return (
        (isPM && hours !== 12
          ? hours + 12
          : hours === 12 && !isPM
          ? 0
          : hours) +
        minutes / 60
      );
    };

    const allIncidentsDataset = [];

    data.forEach((categoryObj) => {
      const normalizedCategory = categoryObj.category
        .toLowerCase()
        .replace(/\s+/g, '');
      categoryObj.incidents.forEach((incident) => {
        const color =
          categoryColors[
            normalizedCategory.toLowerCase().replace(/[^a-z0-9]/g, '')
          ] || 'gray';

        const dataPoint = {
          x: parseTime(incident.timestamp),
          y: categoryMap[categoryObj.category],
          label: `${categoryObj.category} - ${incident.timestamp}`,
          rawData: incident,
          categoryColor: color,
        };

        allIncidentsDataset.push(dataPoint);
      });
    });

    return {
      datasets: [
        {
          label: 'Incidents',
          data: allIncidentsDataset,
          pointRadius: 6,
          pointBackgroundColor: (ctx) => ctx.raw?.categoryColor || 'gray',
        },
      ],
      categoryMap,
    };
  }, [data]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event) => {
        const chart = chartRef.current;
        if (!chart) return;

        const points = chart.getElementsAtEventForMode(
          event,
          'nearest',
          { intersect: true },
          true
        );

        if (points.length) {
          const datasetIndex = points[0].datasetIndex;
          const dataIndex = points[0].index;
          const clickedData = chartData.datasets[datasetIndex].data[dataIndex];
          const selectedId = clickedData.rawData.id;
          setFilter((prev) => ({ ...prev, id: selectedId }));
          setShowIncidentDetailsModal((prev) => ({ ...prev, show: true }));
        }
      },
      scales: {
        x: {
          type: 'linear',
          title: { display: true, text: 'Time (hh:mm)' },
          ticks: {
            callback: (value) => {
              const hours = Math.floor(value);
              const minutes = Math.round((value - hours) * 60);
              return `${hours}:${minutes.toString().padStart(2, '0')}`;
            },
          },
          grid: { display: false },
        },
        y: {
          type: 'linear',
          ticks: {
            callback: (value) =>
              Object.keys(chartData.categoryMap || {}).find(
                (key) => chartData.categoryMap[key] === value
              ) || '',
          },
          grid: { display: false },
        },
      },

      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const rawTime = tooltipItem.raw.rawData.timestamp;
              return `${tooltipItem.raw.rawData.stages_of_exam} ${rawTime}`;
            },
          },
        },
      },
    }),
    [chartData.categoryMap]
  );

  if (!data || data.length === 0) {
    return (
      <div
        style={{ height, width: '100%' }}
        className='d-flex justify-content-center align-items-center text-center'
      >
        No data available, Please select exam day/shift filters to see past exam
        days data.
      </div>
    );
  }

  return (
    <div className='d-flex justify-content-center align-items-center py-3 overflow-scroll'>
      {isLoading ? (
        <Loader />
      ) : (
        <Scatter
          ref={chartRef}
          options={{
            ...options,
            responsive: false,
            maintainAspectRatio: false,
          }}
          data={chartData}
          height={height}
          width={width}
        />
      )}
      {showIncidentDetailsModal.show && (
        <IncidentListingModal
          showIncidentDetailsModal={showIncidentDetailsModal}
          setShowIncidentDetailsModal={setShowIncidentDetailsModal}
          incidents={modalData}
          selectedStage={filter?.id}
        />
      )}
    </div>
  );
};

export default MinsitryScatterChart;
